import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { History, ShieldCheck, Download, Filter } from "lucide-react";
import logger from "@/lib/logger";

/**
 * ALCOA+ audit trail viewer with e-signature list.
 *
 * Reads /api/audit/{entityType}/{entityId} and /api/esign/{entityType}/{entityId}.
 *
 * Enhancements (2026-08):
 *   - Text filter across action / user / reason / before / after values so
 *     inspectors can drill into a specific event quickly.
 *   - CSV download button — exports the *filtered* view, so what the user
 *     sees is what they get. Uses a client-side blob URL so nothing hits
 *     the network again.
 */
export default function AuditTrailPanel({ entityType, entityId, refreshKey = 0 }) {
  const [audit, setAudit] = useState([]);
  const [sigs, setSigs] = useState([]);
  const [query, setQuery] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [a, s] = await Promise.all([
          api.get(`/audit/${entityType}/${entityId}`),
          api.get(`/esign/${entityType}/${entityId}`),
        ]);
        setAudit(a.data);
        setSigs(s.data);
      } catch (err) {
        logger.warn("AuditTrailPanel fetch failed:", err?.response?.status);
      }
    })();
  }, [entityType, entityId, refreshKey]);

  // Case-insensitive filter across the useful fields. Empty query = all rows.
  const filteredAudit = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return audit;
    return audit.filter((e) => {
      const bag = [
        e.action,
        e.user_name, e.user_email,
        e.reason,
        e.before ? JSON.stringify(e.before) : "",
        e.after ? JSON.stringify(e.after) : "",
      ].join(" ").toLowerCase();
      return bag.includes(q);
    });
  }, [audit, query]);

  const downloadCsv = () => {
    // RFC 4180 CSV — quote every field, escape internal quotes by doubling.
    const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const header = ["timestamp", "action", "user_email", "user_name", "reason", "before", "after"];
    const rows = filteredAudit.map((e) => [
      e.timestamp,
      e.action,
      e.user_email || "",
      e.user_name || "",
      e.reason || "",
      e.before ? JSON.stringify(e.before) : "",
      e.after ? JSON.stringify(e.after) : "",
    ]);
    const csv = [header, ...rows].map((r) => r.map(escape).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `audit_${entityType}_${entityId}_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="audit-panel">
      <div className="vq-card p-5">
        <div className="flex items-center gap-2 mb-3">
          <History className="w-4 h-4 text-slate-500" />
          <h3 className="font-display font-bold text-sm text-slate-900">Audit Trail (ALCOA+)</h3>
          <span className="text-[10px] font-mono text-slate-500 ml-auto">
            {filteredAudit.length}{query ? ` / ${audit.length}` : ""} events
          </span>
        </div>

        {/* Filter + CSV export toolbar. Kept compact so it doesn't dominate
             the panel when there's only a handful of events. */}
        {audit.length > 0 && (
          <div className="flex items-center gap-2 mb-3">
            <div className="relative flex-1">
              <Filter className="w-3.5 h-3.5 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
              <Input
                data-testid="audit-filter-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Filter by action, user, reason…"
                className="h-8 pl-7 text-xs font-mono"
              />
            </div>
            <Button
              data-testid="audit-csv-export"
              onClick={downloadCsv}
              disabled={filteredAudit.length === 0}
              variant="outline"
              className="h-8 px-2 border-slate-300 text-xs font-semibold"
              title="Download the filtered rows as CSV"
            >
              <Download className="w-3.5 h-3.5 mr-1" /> CSV
            </Button>
          </div>
        )}

        <div className="space-y-3 max-h-80 overflow-y-auto">
          {filteredAudit.length === 0 ? (
            <div className="text-xs text-slate-500">
              {query ? "No events match this filter." : "No events yet."}
            </div>
          ) : filteredAudit.map((e) => (
            <div key={e.audit_id} className="border-l-2 border-slate-200 pl-3 py-1">
              <div className="flex items-baseline gap-2">
                <span className="font-mono text-[10px] uppercase tracking-wider text-[#0055FF]">{e.action}</span>
                <span className="text-[10px] font-mono text-slate-400">{new Date(e.timestamp).toLocaleString()}</span>
              </div>
              <div className="text-xs text-slate-700 mt-0.5">
                <span className="font-medium">{e.user_name || e.user_email}</span>
                {e.reason && <> • <span className="italic text-slate-500">&ldquo;{e.reason}&rdquo;</span></>}
              </div>
              {(e.before || e.after) && (
                <div className="text-[10px] font-mono text-slate-400 mt-1">
                  {e.before && <>before: {JSON.stringify(e.before)}</>}
                  {e.after && <> → after: {JSON.stringify(e.after)}</>}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="vq-card p-5">
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck className="w-4 h-4 text-slate-500" />
          <h3 className="font-display font-bold text-sm text-slate-900">E-Signatures (21 CFR Part 11)</h3>
          <span className="text-[10px] font-mono text-slate-500 ml-auto">{sigs.length} sigs</span>
        </div>
        <div className="space-y-3 max-h-80 overflow-y-auto">
          {sigs.length === 0 ? (
            <div className="text-xs text-slate-500">No signatures yet.</div>
          ) : sigs.map((s) => (
            <div key={s.signature_id} className="border border-slate-200 rounded p-3">
              <div className="flex items-baseline gap-2 mb-1">
                <span className="font-mono text-[10px] uppercase tracking-wider text-emerald-700">{s.meaning}</span>
                <span className="text-[10px] font-mono text-slate-400 ml-auto">{new Date(s.signed_at).toLocaleString()}</span>
              </div>
              <div className="text-xs font-semibold text-slate-900">{s.signer_name || s.signer_email}</div>
              <div className="text-[10px] font-mono text-slate-500 mt-0.5">{s.signer_roles?.join(", ")}</div>
              <div className="text-xs text-slate-600 italic mt-1">&ldquo;{s.reason}&rdquo;</div>
              <div className="text-[9px] font-mono text-slate-400 mt-2">{s.signature_id}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
